import React, { useState, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import HotelActionsMenu from "./HotelActionsMenu";
import HotelPerformanceCell from "./HotelPerformanceCell";
import HotelStatusCell from "./HotelStatusCell";
import ClickableFeatureStatus from "./ClickableFeatureStatus";
import ClickableTag from "./ClickableTag";
import { formatInstalledSince, formatLastCheck } from "./utils/homeUtils";

const formatNumber = (value) => (value != null && value !== "" ? Number(value).toLocaleString() : "—");

const HotelRow = ({ hotel, onUpdateStatus, onGenerateSnapshot, onEditDescription, onEditHotel, loadingStats, onUpdateFeatureStatus, onUpdateTag, showLuniShowerColumns = true, showGreenMilesColumns = true, isGeneralList = false, hasAnyLuniShower = false, hasAnyGreenMiles = false, hasAnyLuniButton = false }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuDirectionUp, setMenuDirectionUp] = useState(false);

  const hasLuniShower = Boolean(hotel.has_lunishower);
  const hasGreenMiles = Boolean(hotel.has_greenmiles);
  const hasLuniButton = Boolean(hotel.has_lunibutton);

  const handleHotelClick = useCallback(() => {
    // `from` pilote le bouton retour de la page hôtel.
    navigate(`/hotels/${hotel.id}`, { state: { hotel, from: "/hotels" } });
  }, [navigate, hotel]);

  const handleMenuClick = useCallback((e) => {
    e.stopPropagation();

    // Récupérer la position du bouton "•••"
    const buttonRect = e.target.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const menuHeight = 200; // Estimation de la hauteur du menu

    // Si le menu dépasse en bas de la fenêtre, on l'affiche vers le haut
    const shouldShowUp = buttonRect.bottom + menuHeight > windowHeight;
    setMenuDirectionUp(shouldShowUp);

    // Ouvrir/fermer le menu
    setMenuOpen((open) => !open);
  }, []);

  const handleMenuClose = useCallback(() => setMenuOpen(false), []);

  return (
    <tr onClick={handleHotelClick} className="cursor-pointer hover:bg-gray-50 relative">
      <td className="px-3 py-3 text-sm font-medium text-gray-900 min-w-0">
        <div className="break-words max-w-full sm:max-w-xs md:max-w-sm lg:max-w-md" title={hotel.name}>
          {hotel.name}
        </div>
      </td>

      <td className="px-2 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
        {onUpdateTag ? <ClickableTag tag={hotel.tag} hotelId={hotel.id} onTagChange={onUpdateTag} /> : <span className="text-gray-500">{hotel.tag || <span className="text-gray-400">—</span>}</span>}
      </td>

      {/* Dans la liste générale, chaque feature a sa propre colonne */}
      {isGeneralList ? (
        <>
          {hasAnyLuniShower && (
            <td className="px-2 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
              {hasLuniShower ? <ClickableFeatureStatus feature="lunishower" status={hotel.lunishower_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} compact={true} /> : <span className="text-gray-400 text-sm">—</span>}
            </td>
          )}
          {hasAnyGreenMiles && (
            <td className="px-2 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
              {hasGreenMiles ? <ClickableFeatureStatus feature="greenmiles" status={hotel.greenmiles_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} compact={true} /> : <span className="text-gray-400 text-sm">—</span>}
            </td>
          )}
          {hasAnyLuniButton && (
            <td className="px-2 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
              {hasLuniButton ? <ClickableFeatureStatus feature="lunibutton" status={hotel.lunibutton_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} compact={true} /> : <span className="text-gray-400 text-sm">—</span>}
            </td>
          )}
        </>
      ) : (
        // Dans les listes filtrées, une seule colonne avec tous les badges
        <td className="px-3 py-3 text-sm" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2 flex-wrap">
            {hasLuniShower && <ClickableFeatureStatus feature="lunishower" status={hotel.lunishower_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} />}
            {hasGreenMiles && <ClickableFeatureStatus feature="greenmiles" status={hotel.greenmiles_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} />}
            {hasLuniButton && <ClickableFeatureStatus feature="lunibutton" status={hotel.lunibutton_status} hotelId={hotel.id} onStatusChange={onUpdateFeatureStatus} />}
            {!hasLuniShower && !hasGreenMiles && !hasLuniButton && <span className="text-gray-400 text-sm">—</span>}
          </div>
        </td>
      )}

      {showLuniShowerColumns && (
        <>
          <td className="px-2 py-3 text-sm text-gray-500">{hasLuniShower ? <HotelPerformanceCell goodPercentages={hotel.good_percentages} loadingStats={loadingStats} /> : <span className="text-gray-400">—</span>}</td>

          <td className="px-2 py-3 text-sm text-gray-500">{hasLuniShower ? <HotelStatusCell roomStatus={hotel.room_status} loadingStats={loadingStats} /> : <span className="text-gray-400">—</span>}</td>
        </>
      )}

      <td className="px-2 py-3 text-sm text-gray-500">
        <div>{hotel.room_count ?? "—"}</div>
      </td>

      {isGeneralList && <td className="px-2 py-3 text-sm text-gray-500 whitespace-nowrap">{formatInstalledSince(hotel.installationDate)}</td>}

      {showGreenMilesColumns && <td className="px-2 py-3 text-sm text-gray-500">{hasGreenMiles ? formatNumber(hotel.nb_greenmiles_stays) : <span className="text-gray-400">—</span>}</td>}

      {showLuniShowerColumns && <td className="px-2 py-3 text-xs text-gray-500">{hasLuniShower && (loadingStats || hotel.last_check === null) ? <span className="text-gray-400">—</span> : hasLuniShower ? formatLastCheck(hotel.last_check) : <span className="text-gray-400">—</span>}</td>}

      <td className="px-2 py-3 text-right relative" onClick={(e) => e.stopPropagation()}>
        {(isGeneralList || hasLuniShower || hasGreenMiles) && (
          <>
            <button onClick={handleMenuClick} className="text-gray-500 hover:text-gray-700 focus:outline-none">
              •••
            </button>
            <HotelActionsMenu hotel={hotel} hasLuniShower={hasLuniShower} hasGreenMiles={hasGreenMiles} isOpen={menuOpen} onClose={handleMenuClose} showUp={menuDirectionUp} onUpdateStatus={onUpdateStatus} onGenerateSnapshot={onGenerateSnapshot} onEditDescription={onEditDescription} onEditHotel={onEditHotel} />
          </>
        )}
      </td>
    </tr>
  );
};

/**
 * Mémoïsé : la page re-rend à chaque frappe dans la recherche, à chaque tri et à chaque
 * réponse d'API. Sans cela, les 83 lignes étaient reconstruites à chaque fois.
 *
 * La comparaison par défaut suffit, à condition que l'appelant respecte deux règles :
 *  - les objets `hotel` inchangés gardent leur référence (cf. `mergeHotelPatches`) ;
 *  - les handlers sont stables (`useCallback` côté hook et côté page).
 */
export default memo(HotelRow);
